import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, OverlayTrigger, Popover } from "react-bootstrap";
import { useSelector } from "react-redux";

export const PensionHolidaySection = ({
  setPensionDrop,
  pension,
  isPensionDrop,
  globalData,
  setHolidaySavingDrop,
  holidaySavings,
  isHolidaySavingDrop,
  setHolidaySavingFee,
  holidaySavingFee,
  setValue,
  pensionAmount,
  setPensionAmount,
  seperatedUserCalculations,
  selectedTab,
  selectedList,
  userDetails,
  prefillingData,
  grossSalary, 
}) => {
  const { t } = useTranslation();
  const pensionOptions = [1000, 1500, 2000, 2500, 3000];
  function isPensionApplicable(ssn) {
    return typeof ssn === "string" && ssn.length === 12 && !ssn.endsWith("000");
  }

  const [localPensionChecked, setLocalPensionChecked] = useState({});
  const [localHolidayChecked, setLocalHolidayChecked] = useState({}); 
  const [showErrorMessage, setShowErrorMessage] = useState({});
  const [pensionHasBeenSelected, setPensionHasBeenSelected] = useState({});
  const [showErrorTooltip, setShowErrorTooltip] = useState({});
  const errorTooltipTimerRef = useRef({});

   const isInvoiceFreelancerEmpty = Array.isArray(prefillingData?.getInvoiceData?.fetchInvoices?.invoiceFreelancer) && prefillingData?.getInvoiceData?.fetchInvoices?.invoiceFreelancer.length === 0;

  const getPensionChecked = (index) => {
    if (isInvoiceFreelancerEmpty) {
      return !!prefillingData?.getInvoiceData?.pension;
    }
    return prefillingData?.getInvoiceData?.fetchInvoices?.invoiceFreelancer?.[index]?.pension !== undefined
      ? !!prefillingData?.getInvoiceData?.fetchInvoices?.invoiceFreelancer?.[index]?.pension
      : !!pension?.[index];
  };

  const getPensionAmount = (index) => {
    // Always prioritize the current pensionAmount state first
    if (pensionAmount?.[index] !== undefined) {
      return pensionAmount[index];
    }
    if (isInvoiceFreelancerEmpty) {
      return prefillingData?.getInvoiceData?.pensionAmount || pensionOptions[0];
    }
    return prefillingData?.getInvoiceData?.fetchInvoices?.invoiceFreelancer?.[index]?.pensionAmount || pensionOptions[0];
  };

  const getHolidayChecked = (index) => {
    if (isInvoiceFreelancerEmpty) {
      return !!prefillingData?.getInvoiceData?.holidaySavings;
    }
    return prefillingData?.getInvoiceData?.fetchInvoices?.invoiceFreelancer?.[index]?.holidaySavings !== undefined
      ? !!prefillingData?.getInvoiceData?.fetchInvoices?.invoiceFreelancer?.[index]?.holidaySavings
      : !!holidaySavings?.[index];
  };

  const getHolidayFee = (index) => {
    // Always prioritize the current holidaySavingFee state first
    if (holidaySavingFee?.[index] !== undefined) {
      return holidaySavingFee[index];
    }
    if (isInvoiceFreelancerEmpty) {
      return prefillingData?.getInvoiceData?.holidaySavingsFee || (globalData?.holidaySavingsFee?.[0]?.globalValue || 0);
    }
    return prefillingData?.getInvoiceData?.fetchInvoices?.invoiceFreelancer?.[index]?.holidaySavingsFee || (globalData?.holidaySavingsFee?.[0]?.globalValue || 0);
  };

   // Check if amount is valid - for multiple freelancers, check individual gross salary
  const getIsAmountValid = (index) => {
    if (selectedList?.length > 1 && seperatedUserCalculations?.length > 0) {
      // For multiple freelancers, check individual gross salary
      const individualGrossSalary = seperatedUserCalculations[index]?.grossInSek || 0;
      return Number(individualGrossSalary) >= 5000.00;
    } else {
      // For single freelancer, use total gross salary
      return Number(grossSalary) >= 5000.00;
    }
  };

  const isAmountValid = getIsAmountValid(selectedTab?.index);

  // Auto-set pension amount and checkbox state when amount is not valid
  useEffect(() => {
    if (!isAmountValid && selectedTab?.index !== undefined) {
      setPensionAmount((prev) => ({ ...prev, [selectedTab.index]: 0 }));
      setLocalPensionChecked(prev => ({ ...prev, [selectedTab.index]: false }));
    }
  }, [isAmountValid, selectedTab?.index, setPensionAmount]);

  // Monitor gross amount changes and show error tooltip if pension was selected
  useEffect(() => {
    // Only show tooltip if:
    // 1. User has explicitly selected pension before
    // 2. Amount is not valid (< 5000)
    // 3. Gross salary is not 0 (to avoid first load)
    const currentGrossSalary = selectedList?.length > 1 && seperatedUserCalculations?.length > 0
      ? seperatedUserCalculations[selectedTab?.index]?.grossInSek || 0
      : Number(grossSalary) || 0;
      
    if (selectedTab?.index !== undefined && 
        pensionHasBeenSelected[selectedTab.index] && 
        !isAmountValid &&
        currentGrossSalary > 0) {
      
      // Clear any existing timer for this user
      if (errorTooltipTimerRef.current[selectedTab.index]) {
        clearTimeout(errorTooltipTimerRef.current[selectedTab.index]);
      }
      
      // Show error tooltip
      setShowErrorTooltip(prev => ({ ...prev, [selectedTab.index]: true }));
      
      // Set timer to hide tooltip after 5 seconds
      errorTooltipTimerRef.current[selectedTab.index] = setTimeout(() => {
        setShowErrorTooltip(prev => ({ ...prev, [selectedTab.index]: false }));
        delete errorTooltipTimerRef.current[selectedTab.index];
      }, 5000);
    }
  }, [isAmountValid, selectedTab?.index, pensionHasBeenSelected, grossSalary, seperatedUserCalculations, selectedList]);

  // Sync local state with global state when switching between freelancers
  useEffect(() => {
    if (selectedTab?.index !== undefined) {
      // Sync pension checkbox state if not already set locally
      if (!localPensionChecked.hasOwnProperty(selectedTab.index)) {
        const currentPensionState = getPensionChecked(selectedTab.index);
        if (currentPensionState) {
          setLocalPensionChecked(prev => ({ ...prev, [selectedTab.index]: currentPensionState }));
        }
      }
      
      // Sync holiday checkbox state if not already set locally
      if (!localHolidayChecked.hasOwnProperty(selectedTab.index)) {
        const currentHolidayState = getHolidayChecked(selectedTab.index);
        if (currentHolidayState) {
          setLocalHolidayChecked(prev => ({ ...prev, [selectedTab.index]: currentHolidayState }));
        }
      }
    }
  }, [selectedTab?.index, pension, holidaySavings, prefillingData]);
  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(errorTooltipTimerRef.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  return (
    <>
      
        <div className="col-custom d-block align-items-center justify-content-between pr-0 pantion_main">
          {userDetails?.country === "Sweden" && isPensionApplicable(userDetails?.ssn) && (
                       <>
                        <OverlayTrigger
                show={showErrorTooltip[selectedTab.index] && !isAmountValid && pensionHasBeenSelected[selectedTab.index]}
                trigger={[]} // Disable all default triggers (hover, focus, click)
                placement="top"
                overlay={
                  <Popover
                    id={`pension-error-popover-${selectedTab.index}`}
                    style={{ margin: 0 }}
                  >
                    <Popover.Body
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        fontSize: '13px',
                        fontWeight: '500',
                        boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)',
                        border: 'none'
                      }}
                    >
                      {t("The pension option is only available if the gross salary is 5000 SEK or more")}
                    </Popover.Body>
                  </Popover>
                }
              >
                 <div>
                         <div className="form-check d-flex align-items-center mr-2">
                           <input
                             className="form-check-input text-dark cursor-pointer"
                             type="checkbox"
                             id={`pension-${selectedTab.index}`}
                            disabled={!isAmountValid}
                              checked={
                               isAmountValid && (localPensionChecked.hasOwnProperty(selectedTab.index)
                                 ? localPensionChecked[selectedTab.index]
                                 : getPensionChecked(selectedTab.index))
                             }
                           onChange={() => {
                               const currentState = localPensionChecked.hasOwnProperty(selectedTab.index)
                                 ? localPensionChecked[selectedTab.index]
                                 : getPensionChecked(selectedTab.index);
                               const newState = !currentState;
                               
                               setLocalPensionChecked(prev => ({ ...prev, [selectedTab.index]: newState }));
                               
                               // Mark that pension has been interacted with for this user
                               if (newState) {
                                 setPensionHasBeenSelected(prev => ({ ...prev, [selectedTab.index]: true }));
                                 
                                 // Show error tooltip if gross amount is insufficient
                                 // Only show if gross salary > 0 (not first load)
                                 const currentGrossSalary = selectedList?.length > 1 && seperatedUserCalculations?.length > 0
                                   ? seperatedUserCalculations[selectedTab?.index]?.grossInSek || 0
                                   : Number(grossSalary) || 0;
                                   
                                 if (!isAmountValid && currentGrossSalary > 0) {
                                   // Clear any existing timer for this user
                                   if (errorTooltipTimerRef.current[selectedTab.index]) {
                                     clearTimeout(errorTooltipTimerRef.current[selectedTab.index]);
                                   }
                                   
                                   // Show error tooltip
                                   setShowErrorTooltip(prev => ({ ...prev, [selectedTab.index]: true }));
                                   
                                   // Set timer to hide tooltip after 5 seconds
                                   errorTooltipTimerRef.current[selectedTab.index] = setTimeout(() => {
                                     setShowErrorTooltip(prev => ({ ...prev, [selectedTab.index]: false }));
                                     delete errorTooltipTimerRef.current[selectedTab.index];
                                   }, 5000);
                                 }
                               }
                               
                               const isValid =
                                 seperatedUserCalculations?.every(
                                   (item) => (item?.grossInSek || 0) + (item?.pensionInSek || 0) >= 5000
                                 );
                               if (!isInvoiceFreelancerEmpty && prefillingData?.getInvoiceData?.fetchInvoices?.invoiceFreelancer?.[selectedTab.index]) {
                                 setPensionDrop((prev) => ({ ...prev, [selectedTab.index]: newState }));
                                 setValue("pension", {
                                   ...pension,
                                   [selectedTab.index]: newState
                                 });
                               } else {
                                 setPensionDrop((prev) => ({ ...prev, [selectedTab.index]: newState }));
                                 setValue("pension", { ...pension, [selectedTab.index]: newState });
                               }
                             }}
                           />
                           <label
                             htmlFor={`pension-${selectedTab.index}`}
                             className="form-check-label pl-2 mt-1 text-primary-custom cursor-pointer"
                           >
                             <b>{t('Pension')}</b>
                              {selectedList?.[selectedTab?.index] && (() => {
                const item = selectedList[selectedTab?.index];
                const firstName = item?.signUpUser?.firstName || item?.name || "";
                const lastName = item?.signUpUser?.lastName || "";
                const fallbackName = item?.name || "";
                const fullName = firstName || fallbackName || "";
                const displayName = lastName ? `${fullName} ${lastName}` : fullName;
                return displayName ? (
                  <span className="ml-2">({displayName})</span>
                ) : null;
              })()}
                                </label>
                           
                            {showErrorMessage[selectedTab.index] && !isAmountValid && (
                  <div 
                    className="ml-2" 
                    style={{
                      color: '#dc3545',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      marginTop: '2px'
                    }}
                  >
                    {t("The pension option is only available if the gross salary is 5000 SEK or more")}
                  </div>
                )}
         
                                <Dropdown
                               style={{
                          visibility: isAmountValid && (localPensionChecked.hasOwnProperty(selectedTab.index)
                            ? localPensionChecked[selectedTab.index]
                            : getPensionChecked(selectedTab.index)) ? "visible" : "hidden"
                        }}
                                className="ml-2"
                                show={!!isPensionDrop?.[selectedTab.index]}
                                onToggle={() => setPensionDrop(prev => ({ ...prev, [selectedTab.index]: !prev?.[selectedTab.index] }))}
               
                                >
                                <Dropdown.Toggle
                                 className="pension-dropdown-toggle"
                                 variant="light"
                                  style={{
                                   minWidth: "90px",
                                   borderRadius: "8px",
                                   fontWeight: "bold",
                                   boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                                   border: "1px solid #d1d5db",
                                   color: "#2b2b2b",
                                   background: "#f9f9f9",
                                   cursor: "pointer",
                                   padding: "6px 18px",
                                   display: "flex",
                                   alignItems: "center",
                                   gap: "8px"
                                 }}
                                  onMouseDown={e => e.stopPropagation()}
                                  disabled={false}
                                >
                                  {isAmountValid ? getPensionAmount(selectedTab.index) : 0} SEK
                                </Dropdown.Toggle>
                                <Dropdown.Menu
                                  style={{
                                    overflow: "auto",
                                    height: "160px",
                                    borderRadius: "10px",
                                    boxShadow: "0 4px 16px rgba(0,0,0,0.10)"
                                  }}
                                >
                                  {pensionOptions.map((value, index) => {
                                    return (
                                      <Dropdown.Item
                                       className="pension-dropdown-item"
                                        key={index}
                                        value={value}
                                        onClick={() => {
                            setPensionAmount((prev) => ({ ...prev, [selectedTab.index]: value }));
                            
                            // Mark that pension has been selected for this user
                            setPensionHasBeenSelected(prev => ({ ...prev, [selectedTab.index]: true }));
                            
                            // Check if gross salary is insufficient and show error tooltip
                            // Only show if gross salary > 0 (not first load)
                            const currentGrossSalary = selectedList?.length > 1 && seperatedUserCalculations?.length > 0
                              ? seperatedUserCalculations[selectedTab?.index]?.grossInSek || 0
                              : Number(grossSalary) || 0;
                              
                            if (!isAmountValid && currentGrossSalary > 0) {
                              // Clear any existing timer for this user
                              if (errorTooltipTimerRef.current[selectedTab.index]) {
                                clearTimeout(errorTooltipTimerRef.current[selectedTab.index]);
                              }
                              
                              // Show error tooltip
                              setShowErrorTooltip(prev => ({ ...prev, [selectedTab.index]: true }));
                              
                              // Set timer to hide tooltip after 5 seconds
                              errorTooltipTimerRef.current[selectedTab.index] = setTimeout(() => {
                                setShowErrorTooltip(prev => ({ ...prev, [selectedTab.index]: false }));
                                delete errorTooltipTimerRef.current[selectedTab.index];
                              }, 5000);
                            }
                          }}
                                        style={{
                                          borderRadius: "6px",
                                          margin: "2px 4px",
                                          fontWeight: "500",
                                          color: "#222",
                                          cursor: "pointer",
                                          transition: "background 0.15s"
                                        }}
                                        onMouseOver={e => e.currentTarget.style.background = "#e6f0fa"}
                                        onMouseOut={e => e.currentTarget.style.background = "transparent"}
                                      >
                                        {value} SEK
                                      </Dropdown.Item>
                                    );
                                  })}
                              </Dropdown.Menu>
                            </Dropdown>
                         </div>
         
                         <div>
                           <span className="my-2" style={{ whiteSpace: 'break-spaces', fontSize: '13px' }}>
                             {seperatedUserCalculations?.filter(Boolean)?.length > 1
                               ? t(
                                   'Note: The pension option is only available if the gross salary is 5000 SEK or more, for each freelancer you choose.'
                                 )
                               : t(
                                   'Note: The pension option is only available if the gross salary is 5000 SEK or more.'
                                 )}
                           </span>
                         </div>
                         </div>
                         </OverlayTrigger>
                       </>
                     )}

          <div className="form-check d-flex align-items-center mr-2 mt-2">
            <input
              className="form-check-input text-dark cursor-pointer"
              type="checkbox"
              id={`holidayPay-${selectedTab?.index}`}
              disabled={false}
              checked={
                localHolidayChecked.hasOwnProperty(selectedTab.index)
                  ? localHolidayChecked[selectedTab.index]
                  : getHolidayChecked(selectedTab.index)
              }
               onChange={() => {
                const currentState = localHolidayChecked.hasOwnProperty(selectedTab.index)
                  ? localHolidayChecked[selectedTab.index]
                  : getHolidayChecked(selectedTab.index);
                const newState = !currentState;
                
                setLocalHolidayChecked(prev => ({ ...prev, [selectedTab.index]: newState }));
                
                if (!isInvoiceFreelancerEmpty && prefillingData?.getInvoiceData?.fetchInvoices?.invoiceFreelancer?.[selectedTab.index]) {
                  setHolidaySavingDrop((prev) => ({ ...prev, [selectedTab?.index]: newState }));
                  setValue("holidaySavings", {
                    ...holidaySavings,
                    [selectedTab?.index]: newState
                  });
                } else {
                  setHolidaySavingDrop((prev) => ({ ...prev, [selectedTab?.index]: newState }));
                  setValue("holidaySavings", { ...holidaySavings, [selectedTab?.index]: newState });
                } 
              }} 
            />
            <label
              className="form-check-label pl-2 mt-1 cursor-pointer text-primary"
              htmlFor={`holidayPay-${selectedTab?.index}`}
            >
              <b>{t("Holiday pay")}</b>
              {selectedList?.[selectedTab?.index] && (() => {
                const item = selectedList[selectedTab?.index];
                const firstName = item?.signUpUser?.firstName || item?.name || "";
                const lastName = item?.signUpUser?.lastName || "";
                const fallbackName = item?.name || "";
                const fullName = firstName || fallbackName || "";
                const displayName = lastName ? `${fullName} ${lastName}` : fullName;
                return displayName ? (
                  <span className="ml-2">({displayName})</span>
                ) : null;
              })()}
            </label>
            <Dropdown
              style={{
                visibility: (localHolidayChecked.hasOwnProperty(selectedTab.index)
                  ? localHolidayChecked[selectedTab.index]
                  : getHolidayChecked(selectedTab.index)) ? "visible" : "hidden",
              }}
              className="ml-2"
              show={isHolidaySavingDrop?.[selectedTab.index]}
              onToggle={() => setHolidaySavingDrop((prev) => ({ ...prev, [selectedTab.index]: !isHolidaySavingDrop?.[selectedTab.index] }))}
            >
              <Dropdown.Toggle
                className="holiday-dropdown-toggle"
                variant="light"
                style={{
                  minWidth: "90px",
                  borderRadius: "8px",
                  fontWeight: "bold",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                  border: "1px solid #d1d5db",
                  color: "#2b2b2b",
                  background: "#f9f9f9",
                  cursor: "pointer",
                  padding: "6px 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px"
                }}
                disabled={false}
              >
                {getHolidayFee(selectedTab?.index)}%
              </Dropdown.Toggle>
              <Dropdown.Menu
                style={{
                  overflow: "auto",
                  height: "160px",
                  borderRadius: "10px",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.10)"
                }}
              >
                {globalData &&
                  globalData.currencies &&
                  globalData?.holidaySavingsFee?.map((value) => (
                    <Dropdown.Item
                      className="holiday-dropdown-item"
                      key={value.id}
                      value={value.globalValue}
                      onClick={() => {
                        setHolidaySavingFee((prev) => ({ ...prev, [selectedTab?.index]: value.globalValue }));
                      }}
                      style={{
                        borderRadius: "6px",
                        margin: "2px 4px",
                        fontWeight: "500",
                        color: "#222",
                        cursor: "pointer",
                        transition: "background 0.15s"
                      }}
                      onMouseOver={e => e.currentTarget.style.background = "#e6f0fa"}
                      onMouseOut={e => e.currentTarget.style.background = "transparent"}
                    >
                      {value.globalValue}%
                    </Dropdown.Item>
                  ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <div>
            <span
              className="my-2"
              style={{ whiteSpace: "break-spaces", fontSize: "13px" }}
            >
              {t(
                "Note: 190 SEK will be deducted from each vacation payout."
              )}
            </span>
          </div>
        </div>
    </>
  );
};